// src/pages/MyFollowups.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { LEAD_STATUSES } from "../utils/constants";
import toast from "react-hot-toast";
import { Search, X, Users, ExternalLink, RotateCcw, AlertCircle, Bell, RefreshCw } from "lucide-react";
import SetReminderModal from "../components/SetReminderModal";
import "./myfollowups.css";

const MyFollowups = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [teamAgents, setTeamAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState(""); // "" | "Follow-up" | "In Conversation"
  const [agentFilter, setAgentFilter] = useState("");
  const [search, setSearch] = useState("");
  const [reminderLead, setReminderLead] = useState(null);

  const fetchFollowups = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    else setRefreshing(true);

    try {
      const { data } = await api.get("/leads/followups");
      setLeads(Array.isArray(data.leads) ? data.leads : []);
      if (Array.isArray(data.teamAgents)) {
        setTeamAgents(data.teamAgents);
      }
    } catch (err) {
      toast.error("Failed to load follow-up leads");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchFollowups();
  }, [fetchFollowups]);

  // Active status counts
  const counts = useMemo(() => {
    let baseList = leads;
    if (agentFilter) {
      baseList = baseList.filter(l => String(l.agentId) === String(agentFilter));
    }
    return {
      all: baseList.length,
      followup: baseList.filter(l => l.status === "Follow-up").length,
      inConv: baseList.filter(l => l.status === "In Conversation").length,
    };
  }, [leads, agentFilter]);

  // Filtered rows for display
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      // Status filter
      if (statusFilter && l.status !== statusFilter) return false;
      // Agent filter (for managers)
      if (agentFilter && String(l.agentId) !== String(agentFilter)) return false;
      // Search
      if (q) {
        const matchesName = (l.name || "").toLowerCase().includes(q);
        const matchesNumber = (l.number || l.phone || "").toLowerCase().includes(q);
        const matchesAgent = (l.agentName || "").toLowerCase().includes(q);
        const matchesRemarks = (l.remarks || "").toLowerCase().includes(q);
        const matchesId = String(l.id).includes(q);
        return matchesName || matchesNumber || matchesAgent || matchesRemarks || matchesId;
      }
      return true;
    });
  }, [leads, statusFilter, agentFilter, search]);

  // Handle status update
  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.put(`/leads/${leadId}`, { status: newStatus });
      // If moved to Customer, Not Interested, or New, remove from active followups view
      if (newStatus !== "Follow-up" && newStatus !== "In Conversation") {
        setLeads((prev) => prev.filter((l) => String(l.id || l._id) !== String(leadId)));
        toast.success(`Lead moved to ${newStatus}`);
      } else {
        setLeads((prev) =>
          prev.map((l) => (String(l.id || l._id) === String(leadId) ? { ...l, status: newStatus } : l))
        );
        toast.success(`Status updated to ${newStatus}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    }
  };

  // Handle inline remarks update
  const handleRemarksBlur = async (leadId, newRemarks) => {
    const trimmed = newRemarks.trim();
    try {
      await api.put(`/leads/${leadId}`, { remarks: trimmed });
      setLeads((prev) =>
        prev.map((l) =>
          String(l.id || l._id) === String(leadId)
            ? { ...l, remarks: trimmed, remarksDate: new Date().toISOString().slice(0, 10) }
            : l
        )
      );
      toast.success("Remarks updated");
    } catch (err) {
      toast.error("Failed to update remarks");
    }
  };

  const hasActiveFilters = Boolean(search || agentFilter || statusFilter);

  // If Admin views this page, inform them it's dedicated to Agents & Managers
  if (user?.role === "Admin") {
    return (
      <div className="crm-page followup-page p-5 text-center">
        <div 
          className="p-5 rounded-4 shadow-sm" 
          style={{ 
            background: "var(--surface)", 
            border: "1px solid var(--border)", 
            maxWidth: 560, 
            margin: "40px auto" 
          }}
        >
          <div className="d-inline-flex p-3 rounded-circle mb-3" style={{ background: "rgba(59, 130, 246, 0.1)" }}>
            <Users size={36} className="text-primary" />
          </div>
          <h3 className="fw-bold mb-2">My Follow-ups</h3>
          <p className="text-muted small mb-4" style={{ lineHeight: 1.6 }}>
            This workbench is reserved specifically for Sales Agents and Team Managers to work through their active follow-ups and live conversations. As an Administrator, you can view, filter, and audit all leads across the organization in the central Leads pipeline.
          </p>
          <button 
            type="button"
            className="btn btn-primary px-4 py-2 fw-semibold" 
            onClick={() => window.location.href = "/dashboard/leads"}
          >
            Go to Leads Management
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="crm-page followup-page">
      {/* ── Header ── */}
      <div className="followup-header">
        <div className="followup-title-area">
          <span className="followup-badge-count">
            <Users size={14} />
            {counts.all} Active Leads ({counts.followup} Follow-up, {counts.inConv} In Conversation)
          </span>
          <span className="followup-sub d-none d-md-inline">
            {user?.role === "Manager"
              ? "Follow-up workbench for your sales team leads requiring immediate attention."
              : "Your personal follow-up pipeline for scheduled client callbacks and conversations."}
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm d-flex align-items-center gap-1"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              fontSize: '12px',
              borderRadius: '8px',
              padding: '5px 10px'
            }}
            onClick={() => fetchFollowups(true)}
            disabled={refreshing}
            title="Refresh follow-ups list"
          >
            <RefreshCw size={12} className={refreshing ? "spin" : ""} /> Refresh
          </button>

          {hasActiveFilters && (
            <button
              type="button"
              className="btn btn-sm d-flex align-items-center gap-1"
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                color: 'var(--muted)',
                fontSize: '12px',
                borderRadius: '8px',
                padding: '5px 10px'
              }}
              onClick={() => {
                setSearch("");
                setAgentFilter("");
                setStatusFilter("");
              }}
            >
              <RotateCcw size={12} /> Reset
            </button>
          )}
        </div>
      </div>

      {/* ── Search & Filter Toolbar ── */}
      <div className="followup-toolbar">
        <div className="followup-toolbar__left">
          {/* Search Box */}
          <div className="followup-search-box">
            <Search size={14} className="followup-search-icon" />
            <input
              type="text"
              className="followup-search-input"
              placeholder="Search by name, phone, #ID, remarks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="followup-search-clear"
                onClick={() => setSearch("")}
                title="Clear search"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Status Segmented Pills */}
          <div className="followup-pills">
            <button
              type="button"
              className={`followup-pill-btn ${statusFilter === "" ? "active" : ""}`}
              onClick={() => setStatusFilter("")}
            >
              All Active ({counts.all})
            </button>
            <button
              type="button"
              className={`followup-pill-btn ${statusFilter === "Follow-up" ? "active" : ""}`}
              onClick={() => setStatusFilter("Follow-up")}
            >
              Follow-up ({counts.followup})
            </button>
            <button
              type="button"
              className={`followup-pill-btn ${statusFilter === "In Conversation" ? "active" : ""}`}
              onClick={() => setStatusFilter("In Conversation")}
            >
              In Conversation ({counts.inConv})
            </button>
          </div>
        </div>

        <div className="followup-toolbar__right">
          {/* Agent Filter Dropdown (Manager & Team Scope) */}
          {user?.role === "Manager" && (
            <Form.Select
              size="sm"
              className="followup-filter-select"
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              style={{ minWidth: 160 }}
            >
              <option value="">All Team Follow-ups</option>
              {teamAgents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} {String(a.id) === String(user.id) ? "(You)" : ""}
                </option>
              ))}
            </Form.Select>
          )}

          <span className="small text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
            Showing {visible.length} of {counts.all}
          </span>
        </div>
      </div>

      {/* ── Follow-up Table ── */}
      <div className="followup-table-card" data-tour="followups-workbench">
        <div className="followup-tablewrap">
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" size="sm" className="me-2 text-primary" />
              <span className="text-muted small">Loading follow-ups...</span>
            </div>
          ) : (
            <table className="followup-table">
              <thead className="followup-thead">
                <tr>
                  <th style={{ width: 75 }}># ID</th>
                  <th style={{ width: 220 }}>Name</th>
                  <th style={{ width: 160 }}>Phone</th>
                  <th style={{ width: 170 }}>Status</th>
                  <th style={{ width: 160 }}>Assigned Agent</th>
                  <th>Remarks</th>
                  <th style={{ width: 130 }}>Last Contact</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="followup-empty-state py-5">
                        <AlertCircle size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
                        <div className="fw-semibold">No follow-ups found</div>
                        <div className="small text-muted mt-1">
                          {hasActiveFilters
                            ? "No active leads match your search query or status filter."
                            : "You currently have no leads marked in Follow-up or In Conversation status."}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  visible.map((lead) => {
                    const id = lead._id || lead.id;
                    return (
                      <tr key={id} className="followup-row">
                        {/* Permanent Lead ID */}
                        <td className="followup-dim" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          #{lead.id}
                        </td>

                        {/* Name + Link + Reminder Bell */}
                        <td>
                          <div className="d-flex align-items-center justify-content-between gap-1">
                            <span
                              className="followup-lead-link text-truncate"
                              onClick={() => window.open(`/dashboard/leads/${id}`, '_blank')}
                              title="Open lead details in new tab"
                              style={{ cursor: "pointer", fontWeight: 600 }}
                            >
                              {lead.name || "No Name"}
                              <ExternalLink size={11} className="ms-1" style={{ opacity: 0.6 }} />
                            </span>

                            <button
                              type="button"
                              className="btn btn-sm p-1 text-muted"
                              title="Set Follow-up Reminder"
                              onClick={() => setReminderLead(lead)}
                              style={{ border: 'none', background: 'transparent', cursor: 'pointer', flexShrink: 0 }}
                            >
                              <Bell size={13} style={{ color: 'var(--primary)' }} />
                            </button>
                          </div>
                        </td>

                        {/* Phone */}
                        <td className="followup-dim">{lead.number || lead.phone || "—"}</td>

                        {/* Status Dropdown */}
                        <td>
                          <Form.Select
                            size="sm"
                            className="followup-select"
                            value={lead.status}
                            onChange={(e) => handleStatusChange(id, e.target.value)}
                          >
                            {LEAD_STATUSES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </Form.Select>
                        </td>

                        {/* Agent */}
                        <td className="followup-dim">
                          <span className="fw-medium">{lead.agentName || "Unassigned"}</span>
                        </td>

                        {/* Editable Remarks */}
                        <td>
                          <div
                            className="followup-remarks-cell"
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={(e) => handleRemarksBlur(id, e.target.innerText)}
                            title="Click to edit remarks directly"
                          >
                            {lead.remarks || "—"}
                          </div>
                        </td>

                        {/* Remarks Date */}
                        <td className="followup-dim">
                          {(lead.remarksDate || lead.updatedAt || "").slice(0, 10) || "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Set Reminder Modal */}
      <SetReminderModal
        show={Boolean(reminderLead)}
        onClose={() => setReminderLead(null)}
        lead={reminderLead}
      />
    </div>
  );
};

export default MyFollowups;
